import { confidentialStorageIsAllowed } from "../lib/session_privacy";
import { Controller } from "@hotwired/stimulus";
import { FloatingTooltipManager } from "../lib/floating_tooltip";

type HtmxSwapEvent = Event & {
    detail?: {
        elt?: Element;
        requestConfig?: {
            parameters?: Record<string, string | number>;
            path?: string;
        };
        shouldSwap?: boolean;
        target?: EventTarget | null;
        xhr?: {
            responseURL?: string;
        };
    };
};

type HtmxConfigRequestEvent = Event & {
    detail?: {
        parameters?: Record<string, string | number>;
        path?: string;
    };
};

type TicketListScrollSnapshot = {
    anchorOffsetTop: number;
    anchorTicketId: string;
    assigneeFilter: string;
    createdAt: number;
    scrollLeft: number;
    scrollTop: number;
    statusFilter: string;
    sortFilter: string;
};

type TicketListMotionEventDetail = {
    duration: number;
    ticketId: string;
    type: "enter" | "move";
};

const SCROLL_SNAPSHOT_STORAGE_KEY = "elron:tickets:list-scroll-snapshot";
const SCROLL_SNAPSHOT_MAX_AGE_MS = 30_000;
const TICKET_ROW_ENTER_DURATION_MS = 180;
const TICKET_ROW_MOVE_DURATION_MS = 220;
const TICKET_ROW_MOTION_ANIMATION_PREFIX = "ticket-row-";
const TICKET_FRAGMENT_IDS = ["tickets-detail-fragment", "tickets-chat-fragment"] as const;
const HTMX_REQUEST_FAILURE_EVENTS = [
    "htmx:responseError",
    "htmx:sendError",
    "htmx:timeout",
    "htmx:swapError",
] as const;
const TICKET_SELECTION_REQUEST_PATH = "/TicketSelectionFragment";
const FINAL_FRAGMENT_RECOVERY_DELAY_MS = 1000;
const TICKET_SELECTION_SKELETON_SELECTOR = "[data-ticket-selection-skeleton-for]";
const TICKET_SELECTION_SKELETON_REVEAL_DELAY_MS = 200;
const TICKET_SELECTION_SKELETON_MIN_VISIBLE_MS = 400;
const TICKET_FINAL_FRAGMENT_REQUEST_PATHS = [
    "/TicketDetailFragment",
    "/TicketChatFragment",
    "/TicketTeamChatFragment",
] as const;

export default class extends Controller<HTMLElement> {
    static targets = ["loadingStatus", "row", "scroll", "topFade"];
    static values = {
        loadingLabel: String,
    };

    declare readonly hasLoadingStatusTarget: boolean;
    declare readonly rowTargets: HTMLElement[];
    declare readonly hasScrollTarget: boolean;
    declare readonly scrollTarget: HTMLElement;
    declare readonly hasTopFadeTarget: boolean;
    declare readonly topFadeTarget: HTMLElement;
    declare readonly loadingLabelValue: string;
    declare readonly loadingStatusTarget: HTMLElement;

    private didInitialSelectedScroll = false;
    private userMovedListScroll = false;
    private readonly noteUserMovedListScroll = (): void => {
        this.userMovedListScroll = true;
    };
    private lastStyledTicketId: string | null = null;
    private loadingAnnouncementTimerId: number | null = null;
    private finalFragmentRecoveryTimerId: number | null = null;
    private selectionSkeletonRevealTimerId: number | null = null;
    private fragmentRevealTimerIds = new Map<string, number>();
    private pendingSelectionTicketId: string | null = null;
    private selectionSkeletonVisibleAt = new Map<string, number>();
    private pendingClickTicketId: string | null = null;
    private previousSelectionUrl: string | null = null;
    private restoringFromHistory = false;
    private rowMotionObserver: MutationObserver | null = null;
    private rowMotionPositions = new Map<string, number>();
    private rowMotionRoot: HTMLElement | null = null;
    private suppressNextRowMotion = false;
    private focusAreaTooltipManager: FloatingTooltipManager | null = null;
    private readonly restoreFromHistory = (event: PopStateEvent): void => {
        this.pendingClickTicketId = null;
        this.previousSelectionUrl = null;
        this.refreshFromLocation();
        if (!event.state?.elronTicketSelection) {
            return;
        }

        const ticketId = this.ticketIdFromUrl(window.location.href);
        const row = this.rowTargets.find((candidate) => candidate.dataset.ticketId === ticketId);
        if (row instanceof HTMLAnchorElement) {
            // Reuse the normal fragment requests without pushing another entry.
            // These entries have no HTMX snapshot: copying a previous snapshot's
            // state restores the wrong ticket, and large lists exceed its quota.
            this.restoringFromHistory = true;
            try {
                row.click();
            } finally {
                this.restoringFromHistory = false;
            }
        } else {
            window.location.reload();
        }
    };
    private readonly clearPendingAfterRequestFailure = (event: Event): void => {
        const detail = (event as HtmxSwapEvent).detail;
        const pendingTicketId = TICKET_FRAGMENT_IDS.map(
            (fragmentId) => document.getElementById(fragmentId)?.dataset.ticketFragmentPendingId,
        ).find((ticketId): ticketId is string => Boolean(ticketId));

        if (!pendingTicketId) {
            return;
        }

        const requestElement = detail?.elt;
        const requestPath =
            detail?.requestConfig?.path ||
            (requestElement instanceof Element ? requestElement.getAttribute("hx-get") || "" : "");
        const isSelectionRequest = requestPath.includes(TICKET_SELECTION_REQUEST_PATH);
        const isFinalFragmentRequest = TICKET_FINAL_FRAGMENT_REQUEST_PATHS.some((path) =>
            requestPath.includes(path),
        );
        const requestTicketId =
            detail?.requestConfig?.parameters?.ticketId ||
            this.ticketIdFromRequestPath(requestPath);

        if (String(requestTicketId || "") !== pendingTicketId) {
            return;
        }

        if (isSelectionRequest) {
            this.cancelFinalFragmentRecovery();
            this.clearPendingFragments(pendingTicketId);
            if (this.pendingClickTicketId === pendingTicketId) {
                this.pendingClickTicketId = null;
            }
            if (this.previousSelectionUrl) {
                window.history.replaceState(window.history.state, "", this.previousSelectionUrl);
                this.previousSelectionUrl = null;
            }
            this.refreshFromLocation();
            return;
        }

        if (isFinalFragmentRequest) {
            this.scheduleFinalFragmentRecovery(pendingTicketId);
        }
    };
    private readonly guardTicketSelectionSwap = (event: Event): void => {
        const detail = (event as HtmxSwapEvent).detail;
        const requestPath = detail?.requestConfig?.path || detail?.xhr?.responseURL || "";
        const requestTicketId = String(
            detail?.requestConfig?.parameters?.ticketId ||
                this.ticketIdFromRequestPath(detail?.xhr?.responseURL || requestPath),
        );
        const currentTicketId =
            this.element.closest<HTMLElement>("#tickets-shell")?.dataset.currentTicketId || "";
        const target = detail?.target;
        const isSelectionRequest = requestPath.includes(TICKET_SELECTION_REQUEST_PATH);
        const isTicketFragmentTarget =
            target instanceof HTMLElement &&
            TICKET_FRAGMENT_IDS.includes(target.id as (typeof TICKET_FRAGMENT_IDS)[number]);

        if (
            !requestTicketId ||
            !currentTicketId ||
            requestTicketId === currentTicketId ||
            (!isSelectionRequest && !isTicketFragmentTarget)
        ) {
            return;
        }

        if (detail) {
            detail.shouldSwap = false;
        }
        event.preventDefault();
    };

    connect(): void {
        this.refreshFromLocation = this.refreshFromLocation.bind(this);
        this.refreshAfterSwap = this.refreshAfterSwap.bind(this);
        this.refreshFromHistoryEvent = this.refreshFromHistoryEvent.bind(this);
        this.configureListRefreshRequest = this.configureListRefreshRequest.bind(this);
        this.rememberBeforeRowRequest = this.rememberBeforeRowRequest.bind(this);
        this.rememberBeforeListSwap = this.rememberBeforeListSwap.bind(this);
        this.restoreAfterListSwap = this.restoreAfterListSwap.bind(this);
        this.restoreAfterSettle = this.restoreAfterSettle.bind(this);
        this.updateTopFade = this.updateTopFade.bind(this);
        this.refreshRowMotionPositions = this.refreshRowMotionPositions.bind(this);

        document.body.addEventListener("htmx:configRequest", this.configureListRefreshRequest);
        document.body.addEventListener("htmx:beforeRequest", this.rememberBeforeRowRequest);
        document.body.addEventListener("htmx:beforeSwap", this.guardTicketSelectionSwap);
        document.body.addEventListener("htmx:beforeSwap", this.rememberBeforeListSwap);
        document.body.addEventListener("htmx:afterSwap", this.refreshAfterSwap);
        document.addEventListener("ihp:auto-refresh-update", this.refreshAfterSwap);
        document.body.addEventListener("htmx:afterSwap", this.restoreAfterListSwap);
        document.body.addEventListener("htmx:afterSettle", this.restoreAfterSettle);
        document.body.addEventListener("htmx:pushedIntoHistory", this.refreshFromHistoryEvent);
        document.body.addEventListener("htmx:replacedInHistory", this.refreshFromHistoryEvent);
        HTMX_REQUEST_FAILURE_EVENTS.forEach((eventName) => {
            document.body.addEventListener(eventName, this.clearPendingAfterRequestFailure);
        });
        window.addEventListener("popstate", this.restoreFromHistory);

        if (this.hasScrollTarget) {
            this.scrollTarget.addEventListener("scroll", this.updateTopFade, {
                passive: true,
            });
            this.scrollTarget.addEventListener("wheel", this.noteUserMovedListScroll, { passive: true });
            this.scrollTarget.addEventListener("touchmove", this.noteUserMovedListScroll, { passive: true });
        }
        window.addEventListener("resize", this.refreshRowMotionPositions);

        this.refreshFromLocation();
        this.markTicketRead(
            this.rowTargets.find((target) => target.getAttribute("aria-current") === "page"),
        );
        this.restoreScrollSnapshotSoon(false);
        this.scrollSelectedRowIntoViewSoon();
        this.updateTopFade();
        this.observeTicketRowMotion();
        this.focusAreaTooltipManager = new FloatingTooltipManager(
            this.element,
            '[data-testid="ticket-nature-indicator"]',
            '[data-testid="ticket-nature-tooltip"]',
        );
        this.focusAreaTooltipManager.connect();
    }

    disconnect(): void {
        document.body.removeEventListener("htmx:configRequest", this.configureListRefreshRequest);
        document.body.removeEventListener("htmx:beforeRequest", this.rememberBeforeRowRequest);
        document.body.removeEventListener("htmx:beforeSwap", this.guardTicketSelectionSwap);
        document.body.removeEventListener("htmx:beforeSwap", this.rememberBeforeListSwap);
        document.body.removeEventListener("htmx:afterSwap", this.refreshAfterSwap);
        document.removeEventListener("ihp:auto-refresh-update", this.refreshAfterSwap);
        document.body.removeEventListener("htmx:afterSwap", this.restoreAfterListSwap);
        document.body.removeEventListener("htmx:afterSettle", this.restoreAfterSettle);
        document.body.removeEventListener("htmx:pushedIntoHistory", this.refreshFromHistoryEvent);
        document.body.removeEventListener("htmx:replacedInHistory", this.refreshFromHistoryEvent);
        HTMX_REQUEST_FAILURE_EVENTS.forEach((eventName) => {
            document.body.removeEventListener(eventName, this.clearPendingAfterRequestFailure);
        });
        window.removeEventListener("popstate", this.restoreFromHistory);

        if (this.hasScrollTarget) {
            this.scrollTarget.removeEventListener("scroll", this.updateTopFade);
            this.scrollTarget.removeEventListener("wheel", this.noteUserMovedListScroll);
            this.scrollTarget.removeEventListener("touchmove", this.noteUserMovedListScroll);
        }
        window.removeEventListener("resize", this.refreshRowMotionPositions);
        this.rowMotionObserver?.disconnect();
        this.rowMotionObserver = null;
        this.rowMotionRoot = null;
        this.rowMotionPositions.clear();
        this.suppressNextRowMotion = false;
        this.cancelFinalFragmentRecovery();
        this.cancelSelectionFeedbackTimers();
        this.clearLoadingAnnouncement();
        this.focusAreaTooltipManager?.disconnect();
        this.focusAreaTooltipManager = null;
    }

    select(event: Event): void {
        if (!this.shouldHandleClick(event)) {
            return;
        }

        const row = event.currentTarget;

        if (!(row instanceof HTMLElement)) {
            return;
        }

        // History may evict large HTML snapshots. Let the URL rebuild the loaded list.
        const historyUrl = new URL(row.getAttribute("href") || window.location.href, window.location.href);
        historyUrl.searchParams.set("limit", String(this.rowTargets.length));
        const nextUrl = `${historyUrl.pathname}${historyUrl.search}`;
        // Push the URL ourselves. HTMX's history snapshot clones the whole page
        // and writes it to sessionStorage, which sits on the click path.
        if (!this.restoringFromHistory && `${window.location.pathname}${window.location.search}` !== nextUrl) {
            this.previousSelectionUrl = window.location.href;
            const state = { ...window.history.state, htmx: false, elronTicketSelection: true };
            window.history.replaceState(state, "", window.location.href);
            window.history.pushState(state, "", nextUrl);
        }
        row.setAttribute("hx-push-url", "false");
        row.setAttribute("hx-swap", "none show:none settle:0");
        const clickedTicketId = row.dataset.ticketId || "";
        this.markTicketRead(row);
        this.cancelFinalFragmentRecovery();
        // Optimistic selection. hx-push-url only updates the address bar after the
        // request settles, so until then a URL-derived refresh would read the PREVIOUS
        // ticket and flicker the selection glow off for a frame. Hold the clicked id as
        // pending and ignore stale URL refreshes until the address bar catches up.
        this.pendingClickTicketId = clickedTicketId;
        this.applySelectedTicketId(clickedTicketId);
        this.showPendingFragments(clickedTicketId);
    }

    private showPendingFragments(ticketId: string): void {
        if (!ticketId) {
            return;
        }

        const fragments = TICKET_FRAGMENT_IDS.flatMap((fragmentId) => {
            const fragment = document.getElementById(fragmentId);
            return fragment instanceof HTMLElement ? [fragment] : [];
        });
        const ticketIsAlreadyRendered =
            fragments.length === TICKET_FRAGMENT_IDS.length &&
            fragments.every((fragment) => fragment.dataset.ticketId === ticketId);

        if (ticketIsAlreadyRendered) {
            // The user can cancel an in-flight B selection by clicking the A
            // content that is still rendered. Clear B's pending state before
            // returning, otherwise A remains inert/aria-busy indefinitely.
            const stalePendingTicketIds = new Set(
                fragments.flatMap((fragment) =>
                    [
                        fragment.dataset.ticketFragmentPendingId,
                        fragment.parentElement?.dataset.ticketFragmentPendingBusyId,
                    ].filter((pendingId): pendingId is string => Boolean(pendingId)),
                ),
            );
            stalePendingTicketIds.forEach((pendingTicketId) => {
                if (pendingTicketId !== ticketId) {
                    this.clearPendingFragments(pendingTicketId);
                }
            });
            return;
        }

        this.startSelectionFeedback(ticketId);
        TICKET_FRAGMENT_IDS.forEach((fragmentId) => {
            const fragment = document.getElementById(fragmentId);

            if (!(fragment instanceof HTMLElement)) {
                return;
            }

            fragment.classList.add("ticket-fragment-pending-host");
            fragment.dataset.ticketFragmentPendingId = ticketId;
            if (!fragment.hasAttribute("inert")) {
                fragment.setAttribute("inert", "");
                fragment.dataset.ticketFragmentPendingInertAdded = "true";
            }

            const busyHost = fragment.parentElement;
            if (
                busyHost &&
                (!busyHost.hasAttribute("aria-busy") ||
                    Boolean(busyHost.dataset.ticketFragmentPendingBusyId))
            ) {
                busyHost.setAttribute("aria-busy", "true");
                busyHost.dataset.ticketFragmentPendingBusyId = ticketId;
            }
        });
        this.scheduleLoadingAnnouncement(ticketId);
    }

    private clearPendingFragments(ticketId: string): void {
        TICKET_FRAGMENT_IDS.forEach((fragmentId) => {
            this.clearPendingFragment(ticketId, fragmentId);
        });
        this.finishSelectionFeedbackIfComplete(ticketId);
    }

    private clearPendingFragment(ticketId: string, fragmentId: string): void {
        const fragment = document.getElementById(fragmentId);

        if (!(fragment instanceof HTMLElement)) {
            return;
        }

        if (fragment.dataset.ticketFragmentPendingId === ticketId) {
            fragment.classList.remove("ticket-fragment-pending-host");
            delete fragment.dataset.ticketFragmentPendingId;
            if (fragment.dataset.ticketFragmentPendingInertAdded === "true") {
                fragment.removeAttribute("inert");
                delete fragment.dataset.ticketFragmentPendingInertAdded;
            }
        }
        this.clearPendingBusyHost(fragment.parentElement, ticketId);
        this.hideSelectionSkeleton(ticketId, fragmentId);
        this.cancelFragmentRevealTimer(fragmentId);
        this.selectionSkeletonVisibleAt.delete(fragmentId);
    }

    private clearPendingBusyHost(busyHost: HTMLElement | null, ticketId: string): void {
        if (!busyHost || busyHost.dataset.ticketFragmentPendingBusyId !== ticketId) {
            return;
        }

        busyHost.removeAttribute("aria-busy");
        delete busyHost.dataset.ticketFragmentPendingBusyId;
    }

    private startSelectionFeedback(ticketId: string): void {
        this.cancelSelectionFeedbackTimers();
        this.pendingSelectionTicketId = ticketId;
        this.selectionSkeletonVisibleAt.clear();
        this.selectionSkeletons().forEach((skeleton) => {
            this.alignSelectionSkeleton(skeleton);
            skeleton.dataset.ticketSelectionSkeletonPendingId = ticketId;
            skeleton.classList.add("ticket-selection-skeleton-pending");
            skeleton.classList.remove("ticket-selection-skeleton-visible");
        });
        this.selectionSkeletonRevealTimerId = window.setTimeout(() => {
            this.selectionSkeletonRevealTimerId = null;
            this.revealSelectionSkeletons(ticketId);
        }, TICKET_SELECTION_SKELETON_REVEAL_DELAY_MS);
    }

    private revealSelectionSkeletons(ticketId: string): void {
        if (this.pendingSelectionTicketId !== ticketId) {
            return;
        }

        this.selectionSkeletons().forEach((skeleton) => {
            const fragmentId = skeleton.dataset.ticketSelectionSkeletonFor || "";
            const fragment = document.getElementById(fragmentId);
            this.alignSelectionSkeleton(skeleton);
            if (
                fragmentId &&
                fragment?.dataset.ticketFragmentPendingId === ticketId &&
                skeleton.dataset.ticketSelectionSkeletonPendingId === ticketId
            ) {
                if (!this.selectionSkeletonVisibleAt.has(fragmentId)) {
                    this.selectionSkeletonVisibleAt.set(fragmentId, performance.now());
                }
                skeleton.classList.add("ticket-selection-skeleton-visible");
            }
        });
    }

    private hideSelectionSkeleton(ticketId: string, fragmentId: string): void {
        this.selectionSkeletons()
            .filter((skeleton) => skeleton.dataset.ticketSelectionSkeletonFor === fragmentId)
            .forEach((skeleton) => {
                if (skeleton.dataset.ticketSelectionSkeletonPendingId !== ticketId) {
                    return;
                }

                skeleton.classList.remove(
                    "ticket-selection-skeleton-pending",
                    "ticket-selection-skeleton-visible",
                );
                delete skeleton.dataset.ticketSelectionSkeletonPendingId;
            });
    }

    private selectionSkeletons(): HTMLElement[] {
        return Array.from(
            document.querySelectorAll<HTMLElement>(TICKET_SELECTION_SKELETON_SELECTOR),
        );
    }

    private alignSelectionSkeleton(skeleton: HTMLElement): void {
        const fragmentId = skeleton.dataset.ticketSelectionSkeletonFor || "";
        if (fragmentId !== "tickets-chat-fragment") {
            skeleton.style.inset = "0px";
            return;
        }

        const pane = skeleton.parentElement;
        const fragment = document.getElementById(fragmentId);
        const tabs = fragment?.querySelector<HTMLElement>("[data-ticket-chat-static-tabs]");
        const composer = fragment?.querySelector<HTMLElement>(
            "[data-ticket-chat-static-composer]",
        );
        if (!pane || !composer) {
            skeleton.style.inset = "0px";
            return;
        }

        const paneRect = pane.getBoundingClientRect();
        const tabsRect = tabs?.getBoundingClientRect();
        const composerRect = composer.getBoundingClientRect();
        const top = tabsRect ? Math.max(0, tabsRect.bottom - paneRect.top) : 0;
        const bottom = Math.max(0, paneRect.bottom - composerRect.top);

        skeleton.style.inset = `${top}px 0px ${bottom}px`;
    }

    private revealPendingFragment(ticketId: string, fragmentId: string): void {
        if (this.pendingSelectionTicketId !== ticketId) {
            return;
        }

        const visibleAt = this.selectionSkeletonVisibleAt.get(fragmentId);
        if (visibleAt === undefined) {
            this.clearPendingFragment(ticketId, fragmentId);
            this.finishSelectionFeedbackIfComplete(ticketId);
            return;
        }

        const remainingVisibleMs = Math.max(
            0,
            visibleAt + TICKET_SELECTION_SKELETON_MIN_VISIBLE_MS - performance.now(),
        );
        if (remainingVisibleMs === 0) {
            this.clearPendingFragment(ticketId, fragmentId);
            this.finishSelectionFeedbackIfComplete(ticketId);
            return;
        }

        if (this.fragmentRevealTimerIds.has(fragmentId)) {
            return;
        }
        const timerId = window.setTimeout(() => {
            this.fragmentRevealTimerIds.delete(fragmentId);
            const fragment = document.getElementById(fragmentId);
            if (
                this.pendingSelectionTicketId === ticketId &&
                fragment?.dataset.ticketId === ticketId
            ) {
                this.clearPendingFragment(ticketId, fragmentId);
                this.finishSelectionFeedbackIfComplete(ticketId);
            }
        }, remainingVisibleMs);
        this.fragmentRevealTimerIds.set(fragmentId, timerId);
    }

    private finishSelectionFeedbackIfComplete(ticketId: string): void {
        const anotherFragmentIsPending = TICKET_FRAGMENT_IDS.some(
            (fragmentId) =>
                document.getElementById(fragmentId)?.parentElement?.dataset
                    .ticketFragmentPendingBusyId === ticketId,
        );
        if (anotherFragmentIsPending || this.pendingSelectionTicketId !== ticketId) {
            return;
        }

        this.cancelSelectionFeedbackTimers();
        this.pendingSelectionTicketId = null;
        this.selectionSkeletonVisibleAt.clear();
        this.cancelFinalFragmentRecovery();
        this.clearLoadingAnnouncement();
    }

    private cancelFragmentRevealTimer(fragmentId: string): void {
        const timerId = this.fragmentRevealTimerIds.get(fragmentId);
        if (timerId !== undefined) {
            window.clearTimeout(timerId);
            this.fragmentRevealTimerIds.delete(fragmentId);
        }
    }

    private cancelSelectionFeedbackTimers(): void {
        if (this.selectionSkeletonRevealTimerId !== null) {
            window.clearTimeout(this.selectionSkeletonRevealTimerId);
            this.selectionSkeletonRevealTimerId = null;
        }
        this.fragmentRevealTimerIds.forEach((timerId) => window.clearTimeout(timerId));
        this.fragmentRevealTimerIds.clear();
    }

    private scheduleFinalFragmentRecovery(ticketId: string): void {
        this.cancelFinalFragmentRecovery();
        this.finalFragmentRecoveryTimerId = window.setTimeout(() => {
            this.finalFragmentRecoveryTimerId = null;
            const pendingTicketId = TICKET_FRAGMENT_IDS.map(
                (fragmentId) =>
                    document.getElementById(fragmentId)?.dataset.ticketFragmentPendingId,
            ).find((candidate): candidate is string => Boolean(candidate));
            const shellTicketId =
                this.element.closest<HTMLElement>("#tickets-shell")?.dataset.currentTicketId || "";

            // A newer click can abort or invalidate the previous fragment while
            // the event that starts the new selection is still propagating.
            // Revalidate after that event has settled before using the hard
            // navigation fallback.
            if (pendingTicketId !== ticketId || shellTicketId !== ticketId) {
                return;
            }

            const selectedRow = this.rowTargets.find((row) => row.dataset.ticketId === ticketId);
            if (selectedRow instanceof HTMLAnchorElement) {
                window.location.assign(selectedRow.href);
            }
        }, FINAL_FRAGMENT_RECOVERY_DELAY_MS);
    }

    private cancelFinalFragmentRecovery(): void {
        if (this.finalFragmentRecoveryTimerId !== null) {
            window.clearTimeout(this.finalFragmentRecoveryTimerId);
            this.finalFragmentRecoveryTimerId = null;
        }
    }

    private scheduleLoadingAnnouncement(ticketId: string): void {
        this.clearLoadingAnnouncement();
        if (!this.hasLoadingStatusTarget || !this.loadingLabelValue) {
            return;
        }

        this.loadingAnnouncementTimerId = window.setTimeout(() => {
            this.loadingAnnouncementTimerId = null;
            const isStillPending = TICKET_FRAGMENT_IDS.some(
                (fragmentId) =>
                    document.getElementById(fragmentId)?.dataset.ticketFragmentPendingId ===
                    ticketId,
            );
            if (isStillPending) {
                this.loadingStatusTarget.textContent = this.loadingLabelValue;
            }
        }, 200);
    }

    private clearLoadingAnnouncement(): void {
        if (this.loadingAnnouncementTimerId !== null) {
            window.clearTimeout(this.loadingAnnouncementTimerId);
            this.loadingAnnouncementTimerId = null;
        }
        if (this.hasLoadingStatusTarget) {
            this.loadingStatusTarget.textContent = "";
        }
    }

    private ticketIdFromRequestPath(requestPath: string): string {
        if (!requestPath) {
            return "";
        }

        try {
            return new URL(requestPath, window.location.origin).searchParams.get("ticketId") || "";
        } catch {
            return "";
        }
    }

    private refreshAfterSwap(event: Event): void {
        const target = (event as HtmxSwapEvent).detail?.target;

        if (!(target instanceof Element)) {
            return;
        }

        if (
            target instanceof HTMLElement &&
            TICKET_FRAGMENT_IDS.includes(target.id as (typeof TICKET_FRAGMENT_IDS)[number])
        ) {
            const swappedTicketId = target.dataset.ticketId || "";
            const pendingTicketId = target.parentElement?.dataset.ticketFragmentPendingBusyId || "";
            if (pendingTicketId && swappedTicketId === pendingTicketId) {
                // The list OOB swap can reconnect this controller while the detail and
                // chat swaps are still in flight. Restore the pending id from the DOM
                // so the new controller instance can release the completed fragments.
                this.pendingSelectionTicketId ??= pendingTicketId;
                this.selectionSkeletons()
                    .filter(
                        (skeleton) =>
                            skeleton.dataset.ticketSelectionSkeletonFor === target.id,
                    )
                    .forEach((skeleton) => this.alignSelectionSkeleton(skeleton));
                target.classList.add("ticket-fragment-pending-host");
                target.dataset.ticketFragmentPendingId = pendingTicketId;
                target.setAttribute("inert", "");
                target.dataset.ticketFragmentPendingInertAdded = "true";
                this.revealPendingFragment(pendingTicketId, target.id);
            } else if (pendingTicketId) {
                target.classList.add("ticket-fragment-pending-host");
                target.dataset.ticketFragmentPendingId = pendingTicketId;
                target.setAttribute("inert", "");
                target.dataset.ticketFragmentPendingInertAdded = "true";
            }
        }

        if (target !== this.element && !this.element.contains(target)) {
            return;
        }

        requestAnimationFrame(this.refreshFromLocation);
    }

    private configureListRefreshRequest(event: Event): void {
        const detail = (event as HtmxConfigRequestEvent).detail;

        if (
            !detail?.parameters ||
            !this.isTicketListRefreshRequest(detail.path || "") ||
            this.rowTargets.length === 0
        ) {
            return;
        }

        const currentTicketId =
            this.element.closest<HTMLElement>("#tickets-shell")?.dataset.currentTicketId || "";
        if (currentTicketId) {
            detail.parameters.selectedTicketId = currentTicketId;
        } else {
            delete detail.parameters.selectedTicketId;
        }

        // Background refreshes carry q in hx-get, whereas the search form
        // supplies it in parameters. Both must retain the loaded page extent.
        const requestUrl = new URL(detail.path!, window.location.origin);
        const requestedSearchQuery = String(
            detail.parameters.q === undefined
                ? requestUrl.searchParams.get("q") || ""
                : detail.parameters.q,
        ).trim();
        const currentSearchQuery = (this.element.dataset.searchQuery || "").trim();
        if (requestedSearchQuery !== currentSearchQuery) {
            // A new search starts a new result set. Carrying the expanded row
            // count across queries bypasses the backend's smaller search page.
            return;
        }

        // Auto-refresh used to replace every loaded page with the first ten rows.
        // Keep the current pagination extent so selection never shrinks the
        // scroll container while the user is interacting with it.
        detail.parameters.limit = this.rowTargets.length;
    }

    private rememberBeforeRowRequest(event: Event): void {
        const detail = (event as HtmxSwapEvent).detail;
        const requestPath = detail?.requestConfig?.path || "";

        if (new URL(requestPath, window.location.origin).pathname === TICKET_SELECTION_REQUEST_PATH) {
            return;
        }

        if (this.isTicketListRefreshRequest(requestPath)) {
            this.rememberScrollSnapshot();
            return;
        }

        const requestElement = detail?.elt;

        if (!(requestElement instanceof Element)) {
            return;
        }

        const infiniteScrollTrigger = requestElement.closest('[data-infinite-prefetch="true"]');
        if (infiniteScrollTrigger && this.element.contains(infiniteScrollTrigger)) {
            // Pagination swaps only the sentinel, so the browser would normally keep
            // the list position. Capture the live position anyway because the global
            // post-swap restore hooks otherwise replay an older snapshot (often 0).
            // Once pagination starts, the live browsing position takes precedence
            // over any delayed initial centering still waiting for list rows.
            this.didInitialSelectedScroll = true;
            this.rememberScrollSnapshot();
            return;
        }

        const row = requestElement.closest('[data-testid="ticket-list-row"]');

        if (row instanceof HTMLElement && this.element.contains(row)) {
            this.rememberScrollSnapshot();
        }
    }

    private rememberBeforeListSwap(event: Event): void {
        const detail = (event as HtmxSwapEvent).detail;
        const target = detail?.target;

        if (
            !this.listRefreshMatchesLiveState(
                detail?.requestConfig?.path || "",
                detail?.requestConfig?.parameters,
                target instanceof Element && target.id === "tickets-list-rows",
            )
        ) {
            // A slower AutoRefresh or list request can finish after the user has
            // changed the search or selected another ticket. Never let that stale
            // result replace the rows currently visible to the user.
            if (detail) {
                detail.shouldSwap = false;
            }
            event.preventDefault();
            return;
        }

        if (
            target === this.element ||
            (target instanceof Element &&
                (target.id === "tickets-list-rows" ||
                    (this.element.contains(target) &&
                        this.isTicketRowsPageRequest(detail?.requestConfig?.path || ""))))
        ) {
            // A delayed page response must preserve where the user is now,
            // not where they were when the prefetch request started.
            this.rememberScrollSnapshot();
        }

        if (
            target instanceof Element &&
            this.rowMotionRoot &&
            (target === this.rowMotionRoot ||
                this.rowMotionRoot.contains(target) ||
                target.contains(this.rowMotionRoot))
        ) {
            // Search, filter, selection refresh, and pagination are direct user
            // navigation. Reserve row motion for passive live updates.
            this.suppressNextRowMotion = true;
        }
    }

    private restoreAfterListSwap(event: Event): void {
        const target = (event as HtmxSwapEvent).detail?.target;

        if (
            target === this.element ||
            (target instanceof Element && target.id === "tickets-list-rows")
        ) {
            // htmx fires afterSwap before the browser paints the morphed rows. Restore
            // synchronously here so the morph's temporary scroll clamp is never visible.
            this.restoreScrollSnapshot(true);
            this.scrollSelectedRowIntoViewSoon();
            // HTMX can replace the rows container for selection refreshes. Start a
            // fresh baseline without animating that navigational swap.
            this.observeTicketRowMotion();
        }
    }

    private restoreAfterSettle(event: Event): void {
        const detail = (event as HtmxSwapEvent).detail;
        const target = detail?.target;
        const requestPath = detail?.requestConfig?.path || "";
        const isListSettle =
            target === this.element ||
            (target instanceof Element && target.id === "tickets-list-rows") ||
            this.isTicketListRefreshRequest(requestPath) ||
            this.isTicketRowsPageRequest(requestPath);

        if (!isListSettle) {
            return;
        }

        this.restoreScrollSnapshotSoon(false);
        // Late list content (fragment swaps, appended row pages) can arrive after
        // connect; keep attempting the initial scroll-to-selected until verified.
        this.scrollSelectedRowIntoViewSoon();
        this.updateTopFade();
        if (this.isTicketRowsPageRequest(requestPath)) {
            // Re-register AutoRefresh with the expanded limit, so its server-side
            // subscription cannot replace loaded pages with the initial ten rows.
            document.body.dispatchEvent(new CustomEvent("ticket-list-refresh"));
        }
    }

    private markTicketRead(row: HTMLElement | undefined): void {
        const url = row?.dataset.ticketReadUrl;

        if (!row || !url || row.dataset.ticketReadPending === "true") {
            return;
        }

        row.dataset.ticketReadPending = "true";
        void fetch(url, { method: "POST", credentials: "same-origin", keepalive: true })
            .then((response) => {
                if (!response.ok) {
                    delete row.dataset.ticketReadPending;
                    return;
                }
                row.querySelectorAll('[data-testid="ticket-unread-badge"]').forEach((badge) =>
                    badge.remove(),
                );
            })
            .catch(() => delete row.dataset.ticketReadPending);
    }

    // Top scroll-fade: only reveal the gradient mask once the list is scrolled,
    // so the first row is never dimmed at rest (mirrors the always-on bottom fade).
    private updateTopFade(): void {
        if (!this.hasTopFadeTarget || !this.hasScrollTarget) {
            return;
        }

        this.topFadeTarget.style.opacity = this.scrollTarget.scrollTop > 6 ? "1" : "0";
    }

    private observeTicketRowMotion(): void {
        const rowMotionRoot = this.element.querySelector("#tickets-list-rows");

        if (!(rowMotionRoot instanceof HTMLElement)) {
            this.rowMotionObserver?.disconnect();
            this.rowMotionObserver = null;
            this.rowMotionRoot = null;
            this.rowMotionPositions.clear();
            return;
        }

        if (rowMotionRoot === this.rowMotionRoot && this.rowMotionObserver) {
            return;
        }

        this.rowMotionObserver?.disconnect();
        this.rowMotionRoot = rowMotionRoot;
        this.refreshRowMotionPositions();
        this.rowMotionObserver = new MutationObserver(() => this.animateTicketRowChanges());
        this.rowMotionObserver.observe(rowMotionRoot, {
            attributeFilter: ["data-ticket-id"],
            attributes: true,
            childList: true,
            subtree: true,
        });
    }

    private animateTicketRowChanges(): void {
        const previousPositions = this.rowMotionPositions;
        const currentRows = this.ticketRowsForMotion();
        const suppressMotion = this.suppressNextRowMotion;
        this.suppressNextRowMotion = false;

        if (suppressMotion) {
            // offsetTop on every row forces layout. A frame callback still lands
            // before the prefetched detail and chat swaps, so wait until after them.
            window.setTimeout(() => this.refreshRowMotionPositions(), 250);
            return;
        }

        const currentPositions = this.captureRowMotionPositions(currentRows);
        this.rowMotionPositions = currentPositions;

        if (
            previousPositions.size === 0 ||
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            return;
        }

        currentRows.forEach((row) => {
            const ticketId = row.dataset.ticketId || "";
            const currentTop = currentPositions.get(ticketId);

            if (!ticketId || currentTop === undefined) {
                return;
            }

            const previousTop = previousPositions.get(ticketId);

            if (previousTop === undefined) {
                this.playTicketRowMotion(
                    row,
                    ticketId,
                    "enter",
                    [
                        { opacity: 0, transform: "translateY(8px)" },
                        { opacity: 1, transform: "translateY(0)" },
                    ],
                    TICKET_ROW_ENTER_DURATION_MS,
                    "cubic-bezier(0.22, 1, 0.36, 1)",
                );
                return;
            }

            const offset = previousTop - currentTop;

            if (Math.abs(offset) < 1) {
                return;
            }

            this.playTicketRowMotion(
                row,
                ticketId,
                "move",
                [{ transform: `translateY(${offset}px)` }, { transform: "translateY(0)" }],
                TICKET_ROW_MOVE_DURATION_MS,
                "cubic-bezier(0.4, 0, 0.2, 1)",
            );
        });
    }

    private playTicketRowMotion(
        row: HTMLElement,
        ticketId: string,
        type: TicketListMotionEventDetail["type"],
        keyframes: Keyframe[],
        duration: number,
        easing: string,
    ): void {
        row.getAnimations()
            .filter((animation) => animation.id.startsWith(TICKET_ROW_MOTION_ANIMATION_PREFIX))
            .forEach((animation) => animation.cancel());

        const animation = row.animate(keyframes, {
            duration,
            easing,
        });
        animation.id = `${TICKET_ROW_MOTION_ANIMATION_PREFIX}${type}`;
        row.dataset.ticketMotion = type;
        row.dispatchEvent(
            new CustomEvent<TicketListMotionEventDetail>("ticket-list-motion", {
                bubbles: true,
                detail: { duration, ticketId, type },
            }),
        );

        void animation.finished
            .catch(() => undefined)
            .finally(() => {
                if (row.dataset.ticketMotion === type) {
                    delete row.dataset.ticketMotion;
                }
            });
    }

    private refreshRowMotionPositions(): void {
        this.rowMotionPositions = this.captureRowMotionPositions(this.ticketRowsForMotion());
    }

    private captureRowMotionPositions(rows: HTMLElement[]): Map<string, number> {
        return new Map(
            rows.flatMap((row) => {
                const ticketId = row.dataset.ticketId;
                return ticketId ? [[ticketId, row.offsetTop] as const] : [];
            }),
        );
    }

    private ticketRowsForMotion(): HTMLElement[] {
        if (!this.rowMotionRoot) {
            return [];
        }

        return Array.from(
            this.rowMotionRoot.querySelectorAll<HTMLElement>(
                ':scope > [data-testid="ticket-list-row"][data-ticket-id]',
            ),
        );
    }

    private refreshFromHistoryEvent(_event: Event): void {
        // Derive selection from the address bar, not the event path: background
        // fragment refreshes can fire history events with non-navigational paths
        // (e.g. TicketChatFragment URLs) that would transiently clear the selection.
        // Scroll restoration belongs to the click and list-swap paths. History can
        // emit more than once for one selection and must not replay a stale snapshot.
        requestAnimationFrame(this.refreshFromLocation);
    }

    private refreshFromLocation(): void {
        const urlTicketId = this.selectedTicketIdForUrl(window.location.href);

        if (this.pendingClickTicketId !== null && urlTicketId !== this.pendingClickTicketId) {
            // Address bar hasn't caught up to the just-clicked ticket yet; keep the
            // optimistic selection instead of reverting to the stale URL.
            return;
        }
        if (urlTicketId && urlTicketId === this.pendingClickTicketId) {
            this.pendingClickTicketId = null;
        }

        this.applySelectedTicketId(urlTicketId);
    }

    private applySelectedTicketId(selectedTicketId: string): void {
        let normalizedSelectedTicketId = selectedTicketId || "";

        if (!normalizedSelectedTicketId) {
            // Background/auto-refresh swaps right after a click can momentarily pass
            // an empty id while the address bar still points at a selected ticket.
            // Trust the URL so the selection glow does not flicker off for a frame.
            normalizedSelectedTicketId = this.ticketIdFromUrl(window.location.href) || "";
        }

        this.element.dataset.currentTicketId = normalizedSelectedTicketId;
        this.updateSelectedTicketStyle(normalizedSelectedTicketId);

        const shell = this.element.closest("#tickets-shell");
        if (shell instanceof HTMLElement) {
            shell.dataset.currentTicketId = normalizedSelectedTicketId;
        }

        this.rowTargets.forEach((row) => {
            if (!(row instanceof HTMLElement)) {
                return;
            }

            const isSelected = row.dataset.ticketId === normalizedSelectedTicketId;
            row.setAttribute("aria-current", isSelected ? "page" : "false");
            this.applyCardState(row, isSelected);
        });
    }

    private applyCardState(row: HTMLElement, isSelected: boolean): void {
        const card = row.querySelector("[data-ticket-selection-card]");

        if (!(card instanceof HTMLElement)) {
            return;
        }

        const selectedClasses = this.classListFromData(card.dataset.ticketSelectionSelectedClasses);
        const unselectedClasses = this.classListFromData(
            card.dataset.ticketSelectionUnselectedClasses,
        );

        card.classList.remove(...selectedClasses, ...unselectedClasses);
        card.classList.add(...(isSelected ? selectedClasses : unselectedClasses));
    }

    private updateSelectedTicketStyle(selectedTicketId: string): void {
        const style = document.querySelector("#tickets-selected-ticket-style");

        if (!(style instanceof HTMLStyleElement)) {
            return;
        }

        // Re-parsing the <style> block re-applies the whole sheet for a frame,
        // which flickers the selected-card glow. Selection is the same id when
        // the post-swap/history refresh re-runs, so skip the redundant rewrite.
        if (this.lastStyledTicketId === selectedTicketId) {
            return;
        }
        this.lastStyledTicketId = selectedTicketId;

        const escapedTicketId = this.cssString(selectedTicketId);

        style.textContent = `
#tickets-list-fragment [data-testid="ticket-list-row"]:not([data-ticket-id="${escapedTicketId}"]) > [data-ticket-selection-card] {
  background-color: var(--card) !important;
  border-color: transparent !important;
  box-shadow: none !important;
}
#tickets-list-fragment [data-testid="ticket-list-row"]:not([data-ticket-id="${escapedTicketId}"]):is(:hover, :focus-visible) > [data-ticket-selection-card] {
  background-color: var(--accent) !important;
  box-shadow: inset 3px 0 0 var(--ring) !important;
}
#tickets-list-fragment [data-testid="ticket-list-row"][data-ticket-id="${escapedTicketId}"] > [data-ticket-selection-card] {
  background-color: var(--card) !important;
  border-color: transparent !important;
  box-shadow: inset 3px 0 0 var(--ring) !important;
}
`;
    }

    private rememberScrollSnapshot(): void {
        const scrollElement = this.scrollElement();

        if (!scrollElement) {
            return;
        }

        const anchor = this.firstVisibleRow(scrollElement);
        const scrollRect = scrollElement.getBoundingClientRect();
        const anchorRect = anchor?.getBoundingClientRect();
        const snapshot: TicketListScrollSnapshot = {
            anchorOffsetTop: anchorRect ? anchorRect.top - scrollRect.top : 0,
            anchorTicketId: anchor?.dataset.ticketId || "",
            assigneeFilter: this.element.dataset.assigneeFilter || "",
            createdAt: Date.now(),
            scrollLeft: scrollElement.scrollLeft,
            scrollTop: scrollElement.scrollTop,
            statusFilter: this.element.dataset.statusFilter || "",
            sortFilter: this.element.dataset.sortFilter || "priority",
        };

        try {
            if (confidentialStorageIsAllowed()) window.sessionStorage.setItem(SCROLL_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
        } catch (_error) {
            return;
        }
    }

    private restoreScrollSnapshotSoon(clearAfterRestore: boolean): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this.restoreScrollSnapshot(clearAfterRestore));
        });
    }

    // On a fresh load (e.g. opening a ticket via a direct link), bring the selected
    // row into view. Runs once; a fresh back-nav scroll snapshot takes precedence.
    // Retries on list swaps because the list rows may arrive after connect().
    private scrollSelectedRowIntoViewSoon(): void {
        if (this.didInitialSelectedScroll) {
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (this.didInitialSelectedScroll) {
                    return;
                }

                const scrollElement = this.scrollElement();
                if (scrollElement && this.userMovedListScroll) {
                    this.didInitialSelectedScroll = true;
                    return;
                }

                // Defer only to a MEANINGFUL back-nav snapshot (user had scrolled).
                // Fragment refreshes during initial load self-snapshot scrollTop=0,
                // which must not cancel the scroll-to-selected on a direct link.
                const snapshot = this.readScrollSnapshot();
                if (
                    snapshot &&
                    this.scrollSnapshotMatchesCurrentList(snapshot) &&
                    snapshot.scrollTop > 4
                ) {
                    this.didInitialSelectedScroll = true;
                    return;
                }

                const selectedTicketId = this.element.dataset.currentTicketId || "";
                if (!selectedTicketId) {
                    return;
                }

                const selectedRow = this.rowTargets.find(
                    (row) => row.dataset.ticketId === selectedTicketId,
                );
                if (!selectedRow) {
                    return;
                }

                this.scrollRowIntoViewVerified(selectedRow, 5);
            });
        });
    }

    // Scroll + verify: load-time snapshot restores can race the initial scroll
    // back to the top, so re-apply until the row actually sits in the viewport.
    private scrollRowIntoViewVerified(row: HTMLElement, attemptsLeft: number): void {
        if (this.didInitialSelectedScroll) {
            return;
        }

        const scrollElement = this.scrollElement();
        if (scrollElement) {
            const rowBounds = row.getBoundingClientRect();
            const bounds = scrollElement.getBoundingClientRect();
            if (rowBounds.top >= bounds.top && rowBounds.bottom <= bounds.bottom) {
                this.didInitialSelectedScroll = true;
                return;
            }
            scrollElement.scrollTop += rowBounds.top - bounds.top;
        } else {
            row.scrollIntoView({ block: "start", behavior: "auto" });
        }
        // Overwrite any load-time snapshot so subsequent htmx settle restores
        // re-apply THIS position instead of pulling back to 0.
        this.rememberScrollSnapshot();

        window.setTimeout(() => {
            const scrollElement = this.scrollElement();
            if (this.userMovedListScroll) {
                this.didInitialSelectedScroll = true;
                return;
            }
            const bounds = (scrollElement || document.documentElement).getBoundingClientRect();
            const rowBounds = row.getBoundingClientRect();
            const atTop = rowBounds.top <= bounds.top + 4 && rowBounds.bottom > bounds.top;

            if (atTop || attemptsLeft <= 1) {
                this.didInitialSelectedScroll = true;
                return;
            }

            this.scrollRowIntoViewVerified(row, attemptsLeft - 1);
        }, 250);
    }

    private restoreScrollSnapshot(clearAfterRestore: boolean): void {
        const scrollElement = this.scrollElement();
        const snapshot = this.readScrollSnapshot();

        if (!scrollElement || !snapshot || !this.scrollSnapshotMatchesCurrentList(snapshot)) {
            return;
        }

        scrollElement.scrollLeft = snapshot.scrollLeft;

        const anchor = snapshot.anchorTicketId
            ? this.rowTargets.find((row) => row.dataset.ticketId === snapshot.anchorTicketId)
            : undefined;

        if (!anchor) {
            scrollElement.scrollTop = snapshot.scrollTop;
            if (clearAfterRestore) {
                this.clearScrollSnapshot();
            }
            return;
        }

        const scrollRect = scrollElement.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();
        const currentAnchorOffset = anchorRect.top - scrollRect.top;
        scrollElement.scrollTop += currentAnchorOffset - snapshot.anchorOffsetTop;
        if (clearAfterRestore) {
            this.clearScrollSnapshot();
        }
    }

    private readScrollSnapshot(): TicketListScrollSnapshot | null {
        let rawSnapshot: string | null = null;

        try {
            rawSnapshot = window.sessionStorage.getItem(SCROLL_SNAPSHOT_STORAGE_KEY);
        } catch (_error) {
            return null;
        }

        if (!rawSnapshot) {
            return null;
        }

        try {
            const parsed = JSON.parse(rawSnapshot) as Partial<TicketListScrollSnapshot>;

            if (
                typeof parsed.createdAt !== "number" ||
                typeof parsed.scrollTop !== "number" ||
                typeof parsed.scrollLeft !== "number"
            ) {
                return null;
            }

            return {
                anchorOffsetTop:
                    typeof parsed.anchorOffsetTop === "number" ? parsed.anchorOffsetTop : 0,
                anchorTicketId: parsed.anchorTicketId || "",
                assigneeFilter: parsed.assigneeFilter || "",
                createdAt: parsed.createdAt,
                scrollLeft: parsed.scrollLeft,
                scrollTop: parsed.scrollTop,
                statusFilter: parsed.statusFilter || "",
                sortFilter: parsed.sortFilter || "priority",
            };
        } catch (_error) {
            return null;
        }
    }

    private scrollSnapshotMatchesCurrentList(snapshot: TicketListScrollSnapshot): boolean {
        if (Date.now() - snapshot.createdAt > SCROLL_SNAPSHOT_MAX_AGE_MS) {
            return false;
        }

        return (
            snapshot.assigneeFilter === (this.element.dataset.assigneeFilter || "") &&
            snapshot.statusFilter === (this.element.dataset.statusFilter || "") &&
            snapshot.sortFilter === (this.element.dataset.sortFilter || "priority")
        );
    }

    private clearScrollSnapshot(): void {
        try {
            window.sessionStorage.removeItem(SCROLL_SNAPSHOT_STORAGE_KEY);
        } catch (_error) {
            return;
        }
    }

    private isTicketListRefreshRequest(rawPath: string): boolean {
        try {
            return new URL(rawPath, window.location.origin).pathname === "/TicketListFragment";
        } catch (_error) {
            return false;
        }
    }

    private isTicketRowsPageRequest(rawPath: string): boolean {
        try {
            return new URL(rawPath, window.location.origin).pathname === "/TicketRowsPage";
        } catch (_error) {
            return false;
        }
    }

    private listRefreshMatchesLiveState(
        rawPath: string,
        parameters?: Record<string, string | number>,
        refreshesRows = false,
    ): boolean {
        const paginatesRows = this.isTicketRowsPageRequest(rawPath);
        if (!this.isTicketListRefreshRequest(rawPath) && !paginatesRows) {
            return true;
        }

        const searchInput = this.element.querySelector<HTMLInputElement>(
            '[data-testid="ticket-list-search-input"]',
        );
        if (!searchInput) {
            return true;
        }

        try {
            const url = new URL(rawPath, window.location.origin);
            if (refreshesRows || paginatesRows) {
                for (const filter of ["assignee", "status", "sort", "type"]) {
                    const requested = parameters?.[filter] === undefined
                        ? url.searchParams.getAll(filter).join(",")
                        : String(parameters[filter]);
                    if (requested !== this.element.getAttribute(`data-${filter}-filter`)) return false;
                }
            }
            if (refreshesRows && !paginatesRows) {
                const requestedLimit = Number(parameters?.limit ?? url.searchParams.get("limit"));
                if (requestedLimit > 0 && requestedLimit < this.rowTargets.length) {
                    // A page arrived while this older registration was in flight.
                    // Retry with the expanded limit; current-size replies may still remove deleted rows.
                    setTimeout(() => document.body.dispatchEvent(new Event("ticket-list-refresh")));
                    return false;
                }
            }
            const requestedQuery = String(parameters?.q ?? url.searchParams.get("q") ?? "");
            const requestedSelectedTicketId = String(parameters?.selectedTicketId ?? url.searchParams.get("selectedTicketId") ?? "");
            const liveSelectedTicketId =
                this.element.closest<HTMLElement>("#tickets-shell")?.dataset.currentTicketId ||
                this.element.dataset.currentTicketId ||
                this.ticketIdFromUrl(window.location.href);

            return (
                requestedQuery.trim() === searchInput.value.trim() &&
                (paginatesRows || requestedSelectedTicketId === liveSelectedTicketId)
            );
        } catch (_error) {
            return true;
        }
    }

    private firstVisibleRow(scrollElement: HTMLElement): HTMLElement | undefined {
        const scrollRect = scrollElement.getBoundingClientRect();

        return this.rowTargets.find((row) => {
            const rowRect = row.getBoundingClientRect();
            return rowRect.bottom > scrollRect.top && rowRect.top < scrollRect.bottom;
        });
    }

    private scrollElement(): HTMLElement | null {
        if (this.hasScrollTarget) {
            return this.scrollTarget;
        }

        const fallback = this.element.querySelector("#tickets-list-scroll");
        return fallback instanceof HTMLElement ? fallback : null;
    }

    private classListFromData(value: string | undefined): string[] {
        return (value || "").split(/\s+/).filter(Boolean);
    }

    private cssString(value: string): string {
        return String(value || "")
            .replace(/\\/g, "\\\\")
            .replace(/"/g, '\\"')
            .replace(/\n/g, "\\a ");
    }

    private ticketIdFromUrl(rawUrl: string): string {
        let url: URL;

        try {
            url = new URL(rawUrl, window.location.origin);
        } catch (_error) {
            return "";
        }

        const ticketId =
            url.searchParams.get("ticketId") ||
            url.searchParams.get("selectedTicketId") ||
            this.uuidFromPath(url.pathname);

        return ticketId || "";
    }

    private selectedTicketIdForUrl(rawUrl: string): string {
        return this.ticketIdFromUrl(rawUrl) || this.element.dataset.selectedTicketId || "";
    }

    private uuidFromPath(pathname: string): string {
        const match = pathname.match(
            /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/,
        );

        return match ? match[0] : "";
    }

    private shouldHandleClick(event: Event): event is MouseEvent {
        return (
            event instanceof MouseEvent &&
            event.button === 0 &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.shiftKey &&
            !event.altKey
        );
    }
}
